// Aufgaben — die Leitung stellt sie, der Mitarbeiter hakt sie ab.
//
// Bewusst EINE Liste statt Tabs: Offenes oben, Erledigtes verblasst darunter.
// Wer eine Aufgabe abarbeiten soll, will keine Navigation lernen.
//
// Zwei Blicke auf dieselbe Liste, gesteuert über die Rolle:
//   • Leitung (Admin/Büro/Dispatch): sieht ALLE Aufgaben, legt an, weist zu.
//   • Mitarbeiter: sieht nur SEINE Aufgaben und kann sie abhaken — sonst nichts.
// Die RLS setzt das serverseitig durch (Migration 035); hier geht es nur darum,
// dem Mitarbeiter keinen Knopf zu zeigen, der ohnehin ins Leere liefe.

import { useMemo, useRef, useState } from "react";
import {
  useQsTasks,
  useSpeichereQs,
  useHakeQsAb,
  useLoescheQs,
  useLadeQsDokumentHoch,
  useTeam,
  type QsTask,
  type QsPrio,
} from "@/lib/qs";
import { useAuth } from "@/stores/authStore";
import { LogoLoader } from "@/components/LogoLoader";
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Segmented,
  Sheet,
  cn,
} from "@/design-system/ui";
import { ModulHeader } from "@/modules/home/ModulHeader";

/** Datum + Uhrzeit, kurz — "17.07. 14:32". */
function stempel(iso: string | null | undefined) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function QsPage() {
  const { data: tasks = [], isLoading } = useQsTasks();
  const rolle = useAuth((s) => s.role);
  const darfStellen = ["admin", "buero", "dispatch"].includes(rolle ?? "");
  const [offen, setOffen] = useState<QsTask | "neu" | null>(null);

  const { offeneListe, erledigteListe } = useMemo(
    () => ({
      offeneListe: tasks.filter((t) => t.status === "offen"),
      erledigteListe: tasks.filter((t) => t.status === "erledigt"),
    }),
    [tasks]
  );

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      <ModulHeader
        titel="Aufgaben"
        untertitel={
          isLoading ? undefined : `${offeneListe.length} offen · ${erledigteListe.length} erledigt`
        }
        aktion={
          darfStellen ? (
            <Button variant="primary" size="sm" onClick={() => setOffen("neu")}>
              + Aufgabe
            </Button>
          ) : undefined
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <LogoLoader size="md" label="Aufgaben werden geladen" />
          </div>
        ) : tasks.length === 0 ? (
          <EmptyState
            title={darfStellen ? "Keine Aufgaben" : "Nichts zu tun"}
            hint={
              darfStellen
                ? "Aufgaben stellen und einem Mitarbeiter zuweisen — Erstellung und Abhaken werden mit Zeitpunkt festgehalten."
                : "Dir ist gerade nichts zugewiesen. Neue Aufgaben erscheinen hier automatisch."
            }
            action={
              darfStellen ? (
                <Button variant="primary" onClick={() => setOffen("neu")}>
                  Erste Aufgabe anlegen
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="space-y-6">
            {offeneListe.length > 0 && (
              <ul className="space-y-2">
                {offeneListe.map((t) => (
                  <li key={t.id}>
                    <AufgabenZeile t={t} darfStellen={darfStellen} onOeffnen={() => setOffen(t)} />
                  </li>
                ))}
              </ul>
            )}

            {erledigteListe.length > 0 && (
              <section>
                <h2 className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-400">
                  Erledigt
                </h2>
                <ul className="space-y-2">
                  {erledigteListe.map((t) => (
                    <li key={t.id}>
                      <AufgabenZeile t={t} darfStellen={darfStellen} onOeffnen={() => setOffen(t)} />
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>

      {offen && (
        <AufgabenSheet
          task={offen === "neu" ? null : offen}
          darfStellen={darfStellen}
          onClose={() => setOffen(null)}
        />
      )}
    </div>
  );
}

function AufgabenZeile({
  t,
  darfStellen,
  onOeffnen,
}: {
  t: QsTask;
  darfStellen: boolean;
  onOeffnen: () => void;
}) {
  const abhaken = useHakeQsAb();
  const erledigt = t.status === "erledigt";
  const ueberfaellig =
    !erledigt && t.due_on && new Date(t.due_on) < new Date(new Date().toDateString());

  return (
    <Card className={cn("flex items-start gap-3 p-3.5", erledigt && "opacity-60")}>
      {/* Abhaken: 44 px Ziel, damit es auch mit Handschuhen trifft. Das Kästchen
          selbst ist kleiner, die Trefferfläche drumherum ist es nicht. */}
      <button
        onClick={() => abhaken.mutate({ id: t.id, erledigt: !erledigt })}
        aria-label={erledigt ? "Wieder öffnen" : "Als erledigt markieren"}
        className={cn(
          "-m-2 grid h-11 w-11 shrink-0 place-items-center",
          "focus-visible:outline-none"
        )}
      >
        <span
          className={cn(
            "grid h-7 w-7 place-items-center rounded-md border-2 text-sm font-bold",
            "transition-colors duration-fast",
            erledigt
              ? "border-gruen-500 bg-gruen-500 text-gruen-on"
              : "border-ink-500 hover:border-gruen-500"
          )}
        >
          {erledigt && "✓"}
        </span>
      </button>

      <button onClick={onOeffnen} className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-2">
          {t.priority === "hoch" && !erledigt && (
            <span className="shrink-0 rounded bg-warn-bg/15 px-1.5 py-0.5 text-xs font-medium text-warn-fg">
              Hoch
            </span>
          )}
          <span
            className={cn(
              "truncate text-[15px] font-medium",
              erledigt ? "text-ink-400 line-through" : "text-ink-100"
            )}
          >
            {t.title}
          </span>
        </div>

        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-ink-400">
          {t.due_on && (
            <span className={cn("tabular-nums", ueberfaellig && "font-medium text-danger-fg")}>
              {ueberfaellig ? "überfällig " : "bis "}
              {new Date(t.due_on).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
            </span>
          )}
          {/* Die Leitung sieht, WEM die Aufgabe gehört. Für den Mitarbeiter ist
              das überflüssig — es ist ohnehin seine eigene Liste. */}
          {darfStellen && t.assignee?.full_name && (
            <span className="truncate">{t.assignee.full_name}</span>
          )}
          {(t.documents?.length ?? 0) > 0 && (
            <span className="text-ink-500">📎 {t.documents!.length}</span>
          )}
        </div>

        {/* Zeitstempel — die Nachvollziehbarkeit, die der Auftrag verlangt.
            Erledigt: wann und von wem. Sonst: wann gestellt. Beides tabular, damit
            die Ziffern nicht tanzen. */}
        <div className="mt-1 text-xs tabular-nums text-ink-500">
          {erledigt && t.done_at ? (
            <>
              erledigt {stempel(t.done_at)}
              {t.erlediger?.full_name && ` · ${t.erlediger.full_name}`}
            </>
          ) : (
            <>gestellt {stempel(t.created_at)}</>
          )}
        </div>
      </button>
    </Card>
  );
}

function AufgabenSheet({
  task,
  darfStellen,
  onClose,
}: {
  task: QsTask | null;
  darfStellen: boolean;
  onClose: () => void;
}) {
  const speichern = useSpeichereQs();
  const loeschen = useLoescheQs();
  const abhaken = useHakeQsAb();
  const hochladen = useLadeQsDokumentHoch();
  const { data: team = [] } = useTeam();
  const dateiRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState(task?.title ?? "");
  const [description, setDescription] = useState(task?.description ?? "");
  const [priority, setPriority] = useState<QsPrio>(task?.priority ?? "normal");
  const [dueOn, setDueOn] = useState(task?.due_on ?? "");
  const [assignedTo, setAssignedTo] = useState(task?.assigned_to ?? "");
  const [loeschFrage, setLoeschFrage] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Ein Mitarbeiter darf die Aufgabe öffnen und lesen, aber nichts an ihr ändern
  // außer sie abzuhaken. Für ihn ist das Sheet ein reiner Blick + ein Haken.
  const nurLesen = !darfStellen;
  const erledigt = task?.status === "erledigt";
  // Eine Aufgabe ohne Zuständigen erscheint bei niemandem — der häufigste Weg,
  // eine Aufgabe ins Nichts zu stellen. Deshalb Pflicht.
  const kannSpeichern = title.trim().length > 2 && !!assignedTo;

  const speichernUndSchliessen = async () => {
    setFehler(null);
    try {
      await speichern.mutateAsync({
        id: task?.id,
        title,
        description,
        priority,
        due_on: dueOn || null,
        assigned_to: assignedTo || null,
      });
      onClose();
    } catch (e) {
      setFehler((e as Error).message);
    }
  };

  const dateiWaehlen = async (f: File | undefined) => {
    if (!f || !task) return;
    setFehler(null);
    try {
      await hochladen.mutateAsync({ taskId: task.id, file: f });
    } catch (e) {
      setFehler(`Upload fehlgeschlagen: ${(e as Error).message}`);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={!task ? "Neue Aufgabe" : nurLesen ? "Meine Aufgabe" : "Aufgabe"}
      footer={
        <div className="space-y-2">
          {fehler && (
            <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {fehler}
            </p>
          )}

          {nurLesen ? (
            // Der Mitarbeiter: ein Knopf, ein Haken. Kein Speichern, kein Löschen.
            <Button
              variant={erledigt ? "ghost" : "primary"}
              size="lg"
              className="w-full"
              loading={abhaken.isPending}
              onClick={async () => {
                setFehler(null);
                try {
                  await abhaken.mutateAsync({ id: task!.id, erledigt: !erledigt });
                  onClose();
                } catch (e) {
                  setFehler((e as Error).message);
                }
              }}
            >
              {erledigt ? "Doch noch offen" : "Als erledigt abhaken"}
            </Button>
          ) : (
            <>
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                disabled={!kannSpeichern}
                loading={speichern.isPending}
                onClick={speichernUndSchliessen}
              >
                Speichern
              </Button>
              {!assignedTo && title.trim().length > 2 && (
                <p className="text-center text-xs text-ink-500">
                  Erst zuweisen — sonst sieht die Aufgabe niemand.
                </p>
              )}

              {task &&
                (loeschFrage ? (
                  <div className="flex gap-2">
                    <Button className="flex-1" onClick={() => setLoeschFrage(false)}>
                      Abbrechen
                    </Button>
                    <Button
                      variant="danger"
                      className="flex-1"
                      loading={loeschen.isPending}
                      onClick={async () => {
                        await loeschen.mutateAsync(task.id);
                        onClose();
                      }}
                    >
                      Wirklich löschen
                    </Button>
                  </div>
                ) : (
                  <Button variant="ghost" size="sm" className="w-full" onClick={() => setLoeschFrage(true)}>
                    Aufgabe löschen
                  </Button>
                ))}
            </>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {nurLesen ? (
          // ── Der Blick des Mitarbeiters: lesen, nicht bearbeiten ──
          <div className="space-y-4">
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-ink-400">Aufgabe</div>
              <div className="mt-1 flex items-center gap-2">
                {task?.priority === "hoch" && (
                  <span className="shrink-0 rounded bg-warn-bg/15 px-1.5 py-0.5 text-xs font-medium text-warn-fg">
                    Hoch
                  </span>
                )}
                <span className="text-[17px] font-semibold text-ink-50">{task?.title}</span>
              </div>
            </div>

            {task?.description && (
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-ink-400">
                  Beschreibung
                </div>
                <p className="mt-1 whitespace-pre-wrap text-[15px] leading-relaxed text-ink-200">
                  {task.description}
                </p>
              </div>
            )}

            {task?.due_on && (
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-ink-400">Fällig</div>
                <p className="mt-1 text-[15px] tabular-nums text-ink-200">
                  {new Date(task.due_on).toLocaleDateString("de-DE", {
                    weekday: "long",
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </p>
              </div>
            )}
          </div>
        ) : (
          // ── Der Editor der Leitung ──
          <>
            <Field label="Aufgabe">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="z.B. Kühlkette dokumentieren"
                autoFocus={!task}
              />
            </Field>

            <Field label="Beschreibung" optional>
              <textarea
                value={description ?? ""}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Was genau ist zu tun?"
                className="w-full rounded-lg border border-ink-700 bg-ink-900 px-3.5 py-3 text-base leading-relaxed text-ink-100 placeholder:text-ink-500 focus:border-gruen-500/60 focus:outline-none focus:ring-2 focus:ring-gruen-500/20"
              />
            </Field>

            <Field label="Priorität">
              <Segmented
                value={priority}
                onChange={setPriority}
                options={[
                  { value: "normal", label: "Normal" },
                  { value: "hoch", label: "Hoch" },
                ]}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Fällig" optional>
                <Input
                  type="date"
                  value={dueOn ?? ""}
                  onChange={(e) => setDueOn(e.target.value)}
                  className="tabular-nums"
                />
              </Field>

              {/* Pflicht: eine Aufgabe ohne Zuständigen erscheint bei niemandem.
                  Deshalb "wer erledigt es?" statt "niemand" als erste Option. */}
              <Field label="Zuständig">
                <select
                  value={assignedTo ?? ""}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  className={cn(
                    "h-11 w-full rounded-lg border bg-ink-900 px-3 text-base focus:outline-none focus:ring-2 focus:ring-gruen-500/20",
                    assignedTo
                      ? "border-ink-700 text-ink-100 focus:border-gruen-500/60"
                      : "border-warn-bg/50 text-ink-400"
                  )}
                >
                  <option value="">wer erledigt es?</option>
                  {team.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.full_name ?? "ohne Namen"}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </>
        )}

        {/* Nachvollziehbarkeit — für BEIDE sichtbar: wer hat gestellt, wann;
            wer hat abgehakt, wann. Genau das, was der Auftrag verlangt. */}
        {task && (
          <div className="rounded-lg border border-ink-700/70 bg-ink-900/60 px-3.5 py-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-ink-400">Gestellt</span>
              <span className="tabular-nums text-ink-200">
                {stempel(task.created_at)}
                {task.ersteller?.full_name && ` · ${task.ersteller.full_name}`}
              </span>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-3">
              <span className="text-ink-400">Erledigt</span>
              <span className="tabular-nums text-ink-200">
                {task.done_at ? (
                  <>
                    {stempel(task.done_at)}
                    {task.erlediger?.full_name && ` · ${task.erlediger.full_name}`}
                  </>
                ) : (
                  <span className="text-ink-500">offen</span>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Dokumente gibt es erst, wenn die Aufgabe existiert — sonst hätte die
            Datei nichts, woran sie hängen könnte. */}
        {/* Dokumente: Der Mitarbeiter darf sie SEHEN und öffnen — er braucht sie
            oft, um die Aufgabe zu erledigen. Anhängen darf nur die Leitung. Eine
            leere Sektion ohne Upload-Knopf würde ihn nur verwirren, deshalb
            erscheint sie bei ihm nur, wenn es auch etwas zu sehen gibt. */}
        {task && (nurLesen ? (task.documents?.length ?? 0) > 0 : true) && (
          <section>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-400">
              Dokumente
            </h3>

            {(task.documents?.length ?? 0) > 0 && (
              <ul className="mb-2 space-y-1.5">
                {task.documents!.map((d) => (
                  <li key={d.id}>
                    <a
                      href={d.file_url ?? "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 rounded-lg border border-ink-700 bg-ink-900 px-3 py-2.5 text-sm text-ink-200 transition-colors duration-fast hover:border-ink-600 hover:text-ink-50"
                    >
                      <span className="shrink-0">📎</span>
                      <span className="truncate">{d.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}

            {!nurLesen && (
              <>
                <input
                  ref={dateiRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => dateiWaehlen(e.target.files?.[0])}
                />
                <Button
                  className="w-full"
                  loading={hochladen.isPending}
                  onClick={() => dateiRef.current?.click()}
                >
                  Datei anhängen
                </Button>
              </>
            )}
          </section>
        )}

        {!task && !nurLesen && (
          <p className="text-sm text-ink-500">
            Dokumente lassen sich anhängen, sobald die Aufgabe gespeichert ist.
          </p>
        )}
      </div>
    </Sheet>
  );
}
